import React, { memo } from "react";

import { classNames } from "helpers";
import { ShowcaseCard, ShowcaseIntro, ShowcaseNotice } from "ui/components/Showcase";

import {
  REGISTRATION_INTRO_COMPANY,
  REGISTRATION_INTRO_STEPS,
  REGISTRATION_INTRO_TENANT_TYPES,
  REGISTRATION_INTRO_TEXT,
} from "./consts";

/**
 * Lijevi plavi panel registracije: kartica novog računa tvrtke, prvo dodano vozilo i
 * obavijest da je račun spreman. Vizualno prati panel prijave.
 */
const RegistrationIntro: React.FC = () => {
  return (
    <div className="hidden lg:flex w-1/2 h-screen">
      <ShowcaseIntro
        sceneKey="registration"
        title={REGISTRATION_INTRO_TEXT.title}
        description={REGISTRATION_INTRO_TEXT.description}
      >
        <ShowcaseCard className="left-[0.5rem] top-0 w-[17rem]" delay={0}>
          <div className="p-5">
            <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Novi račun</div>

            {/* Vrsta korisnika kao na obrascu: tvrtka ili osoba. */}
            <div className="mt-2 flex rounded-md bg-slate-100 p-0.5">
              {REGISTRATION_INTRO_TENANT_TYPES.map((type, index) => (
                <span
                  key={type}
                  className={classNames(
                    "flex-1 rounded py-1 text-center text-[0.65rem] font-semibold",
                    index === 0 ? "bg-white text-blue-700 shadow-sm" : "text-slate-400"
                  )}
                >
                  {type}
                </span>
              ))}
            </div>

            <div className="mt-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 text-xs font-bold text-white flex-shrink-0">
                {REGISTRATION_INTRO_COMPANY.initials}
              </span>
              <div className="min-w-0">
                <div className="text-sm font-bold text-slate-800 truncate">{REGISTRATION_INTRO_COMPANY.name}</div>
                <div className="text-[0.65rem] text-slate-400 truncate">{REGISTRATION_INTRO_COMPANY.email}</div>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
              <span className="text-[0.7rem] text-slate-400">OIB</span>
              <span className="text-sm font-bold tracking-wide text-slate-800">{REGISTRATION_INTRO_COMPANY.taxNumber}</span>
            </div>
          </div>
        </ShowcaseCard>

        <ShowcaseCard className="right-[-0.5rem] top-[5rem] w-[14rem]" delay={0.25}>
          <div className="relative h-[5.5rem] p-2 rounded-t-lg bg-gradient-to-br from-amber-200 to-amber-300 overflow-hidden">
            <div className="absolute -right-4 -top-6 w-20 h-20 rounded-full bg-white/30" />
            <img
              src="/media/images/vehicles/vehicle-2.png"
              alt=""
              className="relative w-full h-full object-contain drop-shadow-xl"
            />
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold text-slate-800">Prvo vozilo</div>
              <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-[0.6rem] font-semibold text-emerald-600">
                Dodano
              </span>
            </div>
            <div className="text-[0.65rem] text-slate-400">Kia Sorento · ZG 1234 AB</div>

            {/* Koraci početka rada: gotovi imaju kvačicu, sljedeći je prazan kružić. */}
            <div className="mt-3 space-y-1.5">
              {REGISTRATION_INTRO_STEPS.map((step) => (
                <div key={step.label} className="flex items-center gap-2">
                  <span
                    className={classNames(
                      "flex items-center justify-center w-4 h-4 rounded-full",
                      step.isDone ? "bg-emerald-100" : "border border-slate-200"
                    )}
                  >
                    {step.isDone && (
                      <svg className="w-2.5 h-2.5" viewBox="0 0 12 12" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M2.5 6.5l2.5 2.5 4.5-5" />
                      </svg>
                    )}
                  </span>
                  <span className={classNames("text-[0.7rem]", step.isDone ? "text-slate-600" : "text-slate-400")}>
                    {step.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </ShowcaseCard>

        <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
          <ShowcaseNotice
            title="Račun je spreman"
            subtitle="Prijavite se i dodajte svoja vozila"
            iconClassName="bg-blue-100"
            icon={
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#1D4ED8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 21h18M5 21V8l7-5 7 5v13" />
                <path d="M9 21v-6h6v6" />
              </svg>
            }
          />
        </ShowcaseCard>
      </ShowcaseIntro>
    </div>
  );
};

export default memo(RegistrationIntro);
